import React, { useCallback, useState } from 'react';
import {
  Box,
  HStack,
  LinePopup,
  NewBasicButton,
  Profile,
  Text,
  VStack,
} from '@design-system/pc';
import { POPUP_Z_INDEX } from '@display-component/pc';
import styled from 'styled-components';
import GridListBox from '@/ui/GridListBox';
import { Consultants } from '@/types';
import { theme } from '@/styles';
import { getAsset } from '@/utils';
import TrackingService from '@/services/tracking';
import { useRouter } from 'next/router';
import LoadImage from '@/components/LoadImage';

interface ConsultantProps {
  data: Consultants[];
  title: string;
  gtmValue?: Record<string, any>;
}

const DummyImg = '/assets/images/favorite-test/img_area_1.png';

const Consultant = ({ title, data, gtmValue }: ConsultantProps) => {
  const parseArray = () => {
    const returnData = [];
    for (let i = 0; i < Math.ceil(data.length / 6); i++) {
      returnData.push(data.slice(i * 6, 6 + i * 6));
    }
    return returnData;
  };

  return (
    <GridListBox
      title={title}
      isAmount
      amountNum={data.length}
      gridNum={1}
      colNum={2}
      imgList={parseArray()}
      disabled={data ? data.length < 7 : false}
      gtmName="shop_consultant"
    >
      {({ item, index }: any) => {
        return (
          <HStack flexWrap="wrap" gap={16}>
            {item.map((item: Consultants, index: number) => (
              <RDCard
                item={item}
                key={`RD-${index}`}
                gtmValue={gtmValue}
                index={index}
              />
            ))}
          </HStack>
        );
      }}
    </GridListBox>
  );
};

export default Consultant;

interface RDCardProps {
  item: Consultants;
  gtmValue?: Record<string, any>;
  index: number;
}

const RDCard = ({ item, gtmValue, index }: RDCardProps) => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);

  const handleClickMoreButton = useCallback(() => {
    router.push(`/expert/${item.code}`);
  }, []);

  return (
    <>
      <RDCardWrapper
        width={243}
        height={84}
        p={16}
        gap={10}
        border="1px solid"
        borderColor="color_chip.gray.100"
        onClick={() => setIsOpen(true)}
        {...TrackingService.tagGTM('shop_consultant_item_button', {
          'shop-consultant-id': item.id,
          ...gtmValue,
        })}
      >
        <RDCardProfile>
          <LoadImage
            src={item?.image?.url || getAsset(DummyImg)}
            width="52px"
            height="52px"
            objectFit="cover"
            alt={`consultants${index}`}
          />
        </RDCardProfile>
        <Box width="100%">
          <RDCardName width={147}>{item.name}</RDCardName>
          <RDCardBelong width={147}>{item.shop.name}</RDCardBelong>
        </Box>
      </RDCardWrapper>

      <LinePopup
        isOpen={isOpen}
        handleClose={() => setIsOpen(false)}
        zIndex={POPUP_Z_INDEX}
      >
        <VStack width={344}>
          <Profile
            size="md"
            src={item?.image?.url}
            defaultImgType="default_person"
            fit="cover"
            mb={28}
          />
          <Text variant="subTitle4" color="color_chip.primary">
            {item.name}
          </Text>
          <Text variant="body2" color="color_chip.gray.600" mt="8px">
            {item.shop.name}
          </Text>
          <Text variant="body1" color="color_chip.primary" mt={16}>
            {item.description}
          </Text>

          {item.isRD && (
            <NewBasicButton
              emphasis="low"
              size="lg"
              width="100%"
              icon={{
                align: 'right',
                variant: 'ic_arrow_right',
              }}
              mt={40}
              onClick={() => handleClickMoreButton()}
            >
              더 많은 정보 확인하기
            </NewBasicButton>
          )}
        </VStack>
      </LinePopup>
    </>
  );
};

const RDCardWrapper = styled(HStack)`
  cursor: pointer;
`;

const RDCardBelong = styled(Text)`
  color: ${theme.colors.neutral_colors.gray[600]};
  ${theme.typography.body2};
  word-break: keep-all;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RDCardName = styled(Text)`
  color: ${theme.colors.accent_colors.primary.default};
  ${theme.typography.subtitle6};
  word-break: keep-all;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RDCardProfile = styled.div`
  width: 52px;
  height: 52px;
  min-width: 52px;
  min-height: 52px;
  overflow: hidden;
  border-radius: 50%;
`;
